import { useMutation } from '@apollo/client';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import gql from 'graphql-tag';
import currency from 'currency.js';
import React, { useState } from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    instructions: {
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2),
      backgroundColor: 'white',
      borderRadius: theme.shape.borderRadius,
      border: '1px solid rgba(0,0,0,0.06)',
      whiteSpace: 'pre-line',
      lineHeight: 1.6,
      fontSize: '1rem',
      color: '#333',
    },
    button: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1.5, 4),
      fontSize: '1rem',
      fontWeight: 600,
      borderRadius: theme.shape.borderRadius,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-1px)',
      },
      '&:disabled': {
        transform: 'none',
      },
    },
    pricesContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
    },
    list: {
      maxWidth: 'max-content',
      width: '100%',
      backgroundColor: 'white',
      borderRadius: theme.shape.borderRadius,
      border: '1px solid rgba(0,0,0,0.06)',
      overflow: 'hidden',
    },
    listItem: {
      transition: 'all 0.2s ease-in-out',
      borderBottom: '1px solid rgba(0,0,0,0.06)',
      '&:last-child': {
        borderBottom: 'none',
      },
      '&:hover': {
        backgroundColor: '#f8f9fa',
        transform: 'translateX(4px)',
      },
      '&.Mui-selected': {
        backgroundColor: '#e3f2fd',
        '&:hover': {
          backgroundColor: '#bbdefb',
        },
      },
    },
    checkbox: {
      color: theme.palette.primary.main,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
    radio: {
      color: theme.palette.primary.main,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
    listItemText: {
      '& .MuiListItemText-primary': {
        fontWeight: 600,
        fontSize: '1rem',
        color: '#333',
      },
      '& .MuiListItemText-secondary': {
        fontSize: '0.875rem',
        color: '#666',
        marginTop: theme.spacing(0.5),
      },
    },
    totalContainer: {
      maxWidth: 400,
      marginLeft: 'auto',
      marginRight: 'auto',
      marginTop: theme.spacing(3),
      backgroundColor: 'white',
      borderRadius: theme.shape.borderRadius,
      border: '1px solid rgba(0,0,0,0.06)',
      overflow: 'hidden',
    },
    totalListItem: {
      borderBottom: '1px solid rgba(0,0,0,0.06)',
      '&:last-child': {
        borderBottom: 'none',
        backgroundColor: '#f8f9fa',
        fontWeight: 600,
      },
    },
  })
);

interface Props {
  track: any;
  currentUser: any;
}

const CHECKOUT_CREATION = gql`
  mutation CreateCheckout($input: TrackCheckoutInput!) {
    checkout: trackPriceCheckout(input: $input) {
      url
    }
  }
`;

function formatRecurringDisplay(price: any) {
  return price.replace('/yr', ' per year').replace('/mo', ' per month');
}

function formatCurrency(amount: number, price: any) {
  return currency(amount, {
    fromCents: true,
    symbol: price.currencySymbol,
    separator: price.currencySeparator,
    decimal: price.currencyDecimalSymbol,
  }).format();
}

function calculateDiscount(price: any, recurringPrices: any) {
  const monthPlan = recurringPrices.find((p) => p.recurringMonths === 1);
  const months = price.recurringMonths;
  const save = months * monthPlan.amountCents - price.amountCents;
  return formatCurrency(save, price);
}

const PaymentStep = ({ track, currentUser }: Props) => {
  const styles = useStyles();

  const [currentPrice, setCurrentPrice] = useState(track.activePrice);
  const isBundle = !!track.activePrice.bundle;
  const bundlePrices = isBundle ? track.activePrice.bundle.prices : [];
  const discounts = isBundle ? track.activePrice.bundle.discounts : [];
  const activePrices = track.activePrices || [];

  const [stripeRedirecting, setStripeRedirecting] = useState(false);
  const [selectedBundlePriceIds, setSelectedBundlePriceIds] = useState(
    bundlePrices.map((p) => p.id)
  );
  const [createCheckout] = useMutation(CHECKOUT_CREATION, {});

  const appliedDiscountPrice =
    discounts.length > 0
      ? discounts.find((d) => d.min === selectedBundlePriceIds.length)
      : null;
  const appliedDiscount = appliedDiscountPrice
    ? appliedDiscountPrice.amountCents
    : 0;
  const subtotal = bundlePrices
    .filter((p) => selectedBundlePriceIds.includes(p.id))
    .reduce((acc, p) => acc + p.amountCents, 0);

  const checkout = () => {
    setStripeRedirecting(true);
    const url = new URL(window.location.href);
    const cancelUrl = url.toString();
    url.searchParams.set('stripeSuccess', 'true');
    const successUrl = url.toString();
    createCheckout({
      variables: {
        input: {
          priceId: currentPrice.id,
          bundlePriceIds: selectedBundlePriceIds,
          successUrl,
          cancelUrl,
        },
      },
    })
      .then(({ data }) => {
        if (data?.checkout?.url) {
          window.location.href = data.checkout.url;
        } else {
          setStripeRedirecting(false);
        }
      })
      .catch(() => {
        setStripeRedirecting(false);
      });
  };

  return (
    <>
      <Box className={styles.instructions}>
        {currentUser ? (
          <>
            Hi <strong>{currentUser.name}</strong>{' '}
            <em> ({currentUser.c2Username || currentUser.email})</em>, we{' '}
          </>
        ) : (
          'We '
        )}
        integrate with Stripe: one of the most secure payment platforms.{'\r\n'}
        {isBundle
          ? 'Select one or more competitions you want to participate:'
          : activePrices.length > 1
          ? 'Choose your payment option and then press the button below to pay with Apple Pay, Google Pay or credit card.'
          : 'Press the button below to pay with Apple Pay, Google Pay or credit card.'}
      </Box>
      {bundlePrices.length > 0 && (
        <>
          <Box className={styles.pricesContainer}>
            <List className={styles.list}>
              {bundlePrices.map((price) => {
                const labelId = `option-${price.id}`;
                const isSelected = selectedBundlePriceIds.includes(price.id);

                return (
                  <ListItem
                    key={price.id}
                    role={undefined}
                    dense
                    button
                    onClick={() =>
                      setSelectedBundlePriceIds((ids) =>
                        ids.includes(price.id)
                          ? ids.filter((id) => id !== price.id)
                          : [...ids, price.id]
                      )
                    }
                    className={`${styles.listItem} ${
                      isSelected ? 'Mui-selected' : ''
                    }`}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        className={styles.checkbox}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={price.name}
                      secondary={price.displayText}
                      className={styles.listItemText}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
          <List className={styles.totalContainer}>
            <ListItem className={styles.totalListItem}>
              <ListItemIcon>
                <strong>Subtotal</strong>
              </ListItemIcon>
              <ListItemText
                style={{ textAlign: 'end' }}
                primary={formatCurrency(subtotal, currentPrice)}
              />
            </ListItem>
            <ListItem className={styles.totalListItem}>
              <ListItemIcon>
                <strong>Discount</strong>
              </ListItemIcon>
              <ListItemText
                style={{ textAlign: 'end' }}
                primary={
                  appliedDiscount
                    ? `-${formatCurrency(appliedDiscount, currentPrice)}`
                    : '-'
                }
              />
            </ListItem>
            <ListItem className={styles.totalListItem}>
              <ListItemIcon>
                <strong>Total</strong>
              </ListItemIcon>
              <ListItemText
                style={{ textAlign: 'end' }}
                primary={formatCurrency(
                  subtotal - appliedDiscount,
                  currentPrice
                )}
              />
            </ListItem>
          </List>
        </>
      )}
      {activePrices.length > 1 && (
        <>
          <Box className={styles.pricesContainer}>
            <List className={styles.list}>
              {activePrices.map((price) => {
                const labelId = `option-${price.id}`;
                const isSelected = price.id === currentPrice.id;

                return (
                  <ListItem
                    key={price.id}
                    role={undefined}
                    dense
                    button
                    onClick={() => setCurrentPrice(price)}
                    className={`${styles.listItem} ${
                      isSelected ? 'Mui-selected' : ''
                    }`}
                  >
                    <ListItemIcon>
                      <Radio
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        className={styles.radio}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={formatRecurringDisplay(price.displayText)}
                      secondary={
                        price.recurringMonths > 1
                          ? `Save ${calculateDiscount(price, activePrices)}`
                          : price.name
                      }
                      className={styles.listItemText}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </>
      )}
      <Button
        className={styles.button}
        variant="contained"
        color="primary"
        disabled={
          stripeRedirecting || (isBundle && selectedBundlePriceIds.length === 0)
        }
        onClick={() => checkout()}
      >
        {stripeRedirecting ? 'Redirecting...' : 'Stripe Checkout'}
      </Button>
    </>
  );
};

export default PaymentStep;
